import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useVariationSelection } from "../../hooks/useVariationSelection";
import { useSyncProductSingleAttributesToUrl } from "../../hooks/useSyncProductSingleAttributesToUrl";
import type { TransformedProduct } from "../../types/productApiTypes";
import ProductInfoGallery from "../common/ProductInfo/ProductInfoGallery";
import ProductInfoDetails from "../common/ProductInfo/ProductInfoDetails/ProductInfoDetails";

// SKELETON IMPORTS
import ProductInfoGallerySkeleton from "../common/ProductInfo/ProductInfoGallerySkeleton";
import ProductInfoDetailsSkeleton from "../common/ProductInfo/ProductInfoDetails/ProductInfoDetailsSkeleton";

// Helper function to parse attributes from URL
const getAttributesFromUrl = (): { [key: string]: string } => {
  const params = new URLSearchParams(window.location.search);
  const attributes: { [key: string]: string } = {};
  for (const [key, value] of params.entries()) {
    if (key.startsWith("attribute_pa_")) {
      const taxonomy = key.replace("attribute_", "");
      attributes[taxonomy] = value;
    }
  }
  return attributes;
};

// --- Skeleton Loader Sub-Component ---
const ProductSingleInfoSkeleton: React.FC = () => (
  <div className="product-single__info-grid">
    <ProductInfoGallerySkeleton />
    <ProductInfoDetailsSkeleton />
  </div>
);

interface ProductSingleInfoProps {
  product: TransformedProduct | null | undefined;
  isProductLoading: boolean;
  isProductError: boolean;
}

const ProductSingleInfo: React.FC<ProductSingleInfoProps> = ({
  product,
  isProductLoading,
  isProductError,
}) => {
  const { t, ready: areTranslationsReady } = useTranslation();

  // Get initial attributes from the URL *once* on component mount using useMemo
  const initialAttributesFromUrl = useMemo(() => getAttributesFromUrl(), []);

  const {
    selectedAttributes,
    selectedVariation,
    handleAttributeSelect,
    isSelectionLoading, // The initial loading state for variations
  } = useVariationSelection(product, initialAttributesFromUrl); // Pass initial state to the hook

  // The URL sync hook is called here, using state from this component
  useSyncProductSingleAttributesToUrl(product, selectedAttributes);

  // --- State 1: Centralized Loading State ---
  // The component is considered "loading" if the main product query is loading,
  // translations are not ready, or the variations are performing their initial load.
  const isComponentLoading =
    isProductLoading || !areTranslationsReady || isSelectionLoading;
  if (isComponentLoading) {
    return <ProductSingleInfoSkeleton />;
  }

  // State 2: isFetching is intentionally not checked here to prevent skeletons on refetch.

  // --- State 3: Error State ---
  if (isProductError) {
    return (
      <div className="info-grid__message info-grid__message--error">
        <h2>{t("productSingle.productSingleInfo.errorLoadingProductTitle")}</h2>
        <p>{t("productSingle.productSingleInfo.errorLoadingProduct")}</p>
      </div>
    );
  }

  // --- State 4: Success State with No Product Data ---
  if (!product) {
    return (
      <div className="info-grid__message">
        <h2>{t("productSingle.productSingleInfo.noProductFoundTitle")}</h2>
        <p>{t("productSingle.productSingleInfo.noProductFound")}</p>
      </div>
    );
  }

  // --- State 5: Success State with Product Data (Happy Path) ---
  return (
    <div className="product-single__info-grid">
      <ProductInfoGallery
        product={product}
        selectedVariation={selectedVariation}
      />
      <ProductInfoDetails
        product={product}
        selectedVariation={selectedVariation}
        selectedAttributes={selectedAttributes}
        onAttributeSelect={handleAttributeSelect}
        showDetailsLink={false}
      />
    </div>
  );
};

export default ProductSingleInfo;
